import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Repeat, ShoppingBag } from 'lucide-react'
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorState,
  FilterToolbar,
  LoadMore,
  SearchField,
  Skeleton,
} from '@shared/design-system/ui'
import { useSalesInfiniteQuery } from '@entities/sale/sale.queries'
import {
  buildSalesRows,
  filterSalesByCurrency,
  filterRowsByView,
  getSaleStatusPresentation,
} from '@entities/sale'
import type { SalesView } from '@entities/sale'
import { SALE_STATUSES } from '@shared/contracts/enums'
import type { Currency, SaleStatus } from '@shared/contracts/enums'
import { useDebounce } from '@shared/lib/use-debounce'
import { DEFAULT_DATE_RANGE } from '@shared/lib/date-range'
import { analyticsPeriod } from '@shared/lib/analytics-period'
import { SalesAnalyticsPanel } from './SalesAnalyticsPanel'
import type { DatePreset, DateRangeValue } from '@shared/lib/date-range'
import { SalesTable } from './SalesTable'
import { SaleDetailDrawer } from './SaleDetailDrawer'
import { ExportButton } from '@shared/ui/ExportButton'
import type { ExportFormat } from '@shared/ui/ExportButton'
import { apiFetchBlob } from '@shared/api/http'
import { exportTimestamp } from '@shared/lib/export-timestamp'
import { triggerDownload } from '@shared/lib/download'
import {
  controls,
  contentCard,
  header,
  headerText,
  page,
  resultCount,
  statusGroup,
  statusOption,
  subtitle,
  title,
  viewTabs,
} from './sales.css'

export interface SalesSearch {
  scope?: 'charge'
  sale?: string
  q?: string
  status?: SaleStatus[]
  view?: SalesView
  currency?: Currency
  preset?: DatePreset
  from?: string
  to?: string
}

interface SalesScreenProps {
  search: SalesSearch
  onSearchChange: (next: SalesSearch) => void
  onViewSubscription: (subscriptionId: string) => void
}

interface DebouncedSearchInputProps {
  value: string
  onCommit: (value: string | undefined) => void
}

const statusOptions: ReadonlyArray<SaleStatus> = SALE_STATUSES

const viewOptions: ReadonlyArray<{ id: SalesView; label: string }> = [
  { id: 'all', label: 'Todas' },
  { id: 'new', label: 'Novas' },
  { id: 'renewals', label: 'Renovações' },
]

function readDateRange(search: SalesSearch): DateRangeValue {
  return {
    preset: search.preset ?? DEFAULT_DATE_RANGE.preset,
    from: search.from ?? null,
    to: search.to ?? null,
  }
}

function pruneSearch(search: SalesSearch): SalesSearch {
  const next: SalesSearch = {}
  if (search.sale) {
    next.sale = search.sale
    if (search.scope === 'charge') next.scope = search.scope
  }
  if (search.q) next.q = search.q
  if (search.status && search.status.length > 0) next.status = search.status
  if (search.view && search.view !== 'all') next.view = search.view
  if (search.currency) next.currency = search.currency
  if (search.preset && search.preset !== DEFAULT_DATE_RANGE.preset) next.preset = search.preset
  if (search.from) next.from = search.from
  if (search.to) next.to = search.to
  return next
}

function DebouncedSearchInput({ value, onCommit }: DebouncedSearchInputProps) {
  const [text, setText] = useState(value)
  const debouncedSearch = useDebounce(text, 300)

  useEffect(() => {
    const trimmed = debouncedSearch.trim()
    if (trimmed === value) {
      return
    }
    onCommit(trimmed || undefined)
  }, [debouncedSearch, onCommit, value])

  return (
    <SearchField
      value={text}
      onChange={setText}
      placeholder="Buscar por comprador, e-mail ou ID da venda"
      ariaLabel="Buscar vendas"
    />
  )
}

export function SalesScreen({ search, onSearchChange, onViewSubscription }: SalesScreenProps) {
  const update = (patch: Partial<SalesSearch>) => {
    onSearchChange(pruneSearch({ ...search, ...patch }))
  }

  const dateRange = readDateRange(search)
  const period = analyticsPeriod(dateRange)
  const dateFrom = period.from
  const dateTo = period.to
  const statuses = search.status ?? []
  const view: SalesView = search.view ?? 'all'

  const filters = {
    status: search.status && search.status.length > 0 ? search.status : undefined,
    q: search.q,
    currency: 'BRL' as const,
    dateFrom,
    dateTo,
  }

  const sales = useSalesInfiniteQuery(filters, !period.error)

  const items = useMemo(
    () => (sales.data ? sales.data.pages.flatMap((entry) => entry.items) : []),
    [sales.data],
  )

  const selectedCurrency = filters.currency
  const currencyItems = useMemo(
    () => filterSalesByCurrency(items, selectedCurrency),
    [items, selectedCurrency],
  )
  const currencyRows = useMemo(() => buildSalesRows(currencyItems), [currencyItems])
  const visibleRows = useMemo(() => filterRowsByView(currencyRows, view), [currencyRows, view])

  const toggleStatus = (status: SaleStatus) => {
    const active = statuses.includes(status)
    const next = active ? statuses.filter((entry) => entry !== status) : [...statuses, status]
    update({ status: next.length > 0 ? next : undefined })
  }

  const setDateRange = (value: DateRangeValue) => {
    update({
      preset: value.preset,
      from: value.preset === 'custom' ? (value.from ?? undefined) : undefined,
      to: value.preset === 'custom' ? (value.to ?? undefined) : undefined,
    })
  }

  const clearAll = () => {
    const base: SalesSearch = { preset: search.preset, from: search.from, to: search.to }
    if (search.sale) base.sale = search.sale
    if (search.view && search.view !== 'all') base.view = search.view
    if (search.currency) base.currency = search.currency
    onSearchChange(base)
  }

  const selectSale = (saleId: string, scope?: 'charge') => {
    update({ sale: saleId, scope })
  }

  const closeSale = () => {
    const next = { ...search }
    delete next.sale
    onSearchChange(pruneSearch(next))
  }

  const filtersActive = Boolean(search.q) || statuses.length > 0

  async function handleExport(format: ExportFormat) {
    const qs = new URLSearchParams()
    qs.set('format', format)
    if (filters.currency) qs.set('currency', filters.currency)
    if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom)
    if (filters.dateTo) qs.set('dateTo', filters.dateTo)
    if (filters.q) qs.set('q', filters.q)
    filters.status?.forEach((s) => qs.append('status', s))
    const blob = await apiFetchBlob(`/sales/export?${qs}`)
    triggerDownload(blob, `vendas-${exportTimestamp()}.${format}`)
  }

  const chips: ReactNode[] = statuses.map((status) => (
    <Chip
      key={`status-${status}`}
      label={getSaleStatusPresentation(status).label}
      onRemove={() => toggleStatus(status)}
    />
  ))

  if (search.q) {
    chips.push(
      <Chip
        key="query"
        label={`Busca: ${search.q}`}
        onRemove={() => {
          update({ q: undefined })
        }}
      />,
    )
  }

  return (
    <div className={page} data-testid="sales-screen">
      <div className={header}>
        <div className={headerText}>
          <h1 className={title}>Vendas</h1>
          <span className={subtitle}>
            Cada venda e o que aconteceu com ela. Renovações de assinatura ficam agrupadas e não
            contam como cliente novo.
          </span>
        </div>
        <ExportButton onExport={handleExport} />
      </div>

      <SalesAnalyticsPanel
        period={period}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        currency={selectedCurrency}
        onCurrencyChange={(currency) => update({ currency })}
      />

      <Card padding="md" className={contentCard}>
        <h2>Histórico de vendas</h2>
        <span className={subtitle}>
          Lista pela última atividade da compra. O acumulado inclui o valor bruto das cobranças da
          compra, antes de taxas e divisões. Busca, status e tipo filtram somente esta lista; os
          indicadores acima consideram pagamentos confirmados.
        </span>
        <div className={viewTabs} role="group" aria-label="Filtrar vendas por tipo">
          {viewOptions.map((tab) => (
            <button
              key={tab.id}
              type="button"
              aria-pressed={view === tab.id}
              className={statusOption({ active: view === tab.id })}
              onClick={() => update({ view: tab.id })}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <FilterToolbar
          chips={chips.length > 0 ? chips : null}
          onClear={filtersActive ? clearAll : undefined}
          resultCount={
            <span className={resultCount}>
              {visibleRows.length} {visibleRows.length === 1 ? 'resultado' : 'resultados'}
            </span>
          }
        >
          <div className={controls}>
            <DebouncedSearchInput
              key={search.q ?? ''}
              value={search.q ?? ''}
              onCommit={(value) => {
                onSearchChange(pruneSearch({ ...search, q: value }))
              }}
            />
            <div className={statusGroup} role="group" aria-label="Filtrar por status">
              {statusOptions.map((status) => {
                const presentation = getSaleStatusPresentation(status)
                const active = statuses.includes(status)
                return (
                  <button
                    key={status}
                    type="button"
                    aria-pressed={active}
                    className={statusOption({ active })}
                    onClick={() => toggleStatus(status)}
                  >
                    {presentation.label}
                  </button>
                )
              })}
            </div>
          </div>
        </FilterToolbar>

        {period.error ? (
          <p className={subtitle}>Complete um período válido para consultar a lista.</p>
        ) : sales.isPending ? (
          <Skeleton height={320} />
        ) : sales.isError ? (
          <ErrorState
            onRetry={() => {
              void sales.refetch()
            }}
          />
        ) : items.length === 0 ? (
          filtersActive ? (
            <EmptyState
              icon={<ShoppingBag size={22} />}
              title="Nada neste filtro"
              description="Nenhuma venda corresponde aos filtros atuais."
              action={
                <Button variant="secondary" onClick={clearAll}>
                  Limpar filtros
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<ShoppingBag size={22} />}
              title="Nenhuma venda nesta moeda"
              description={`Não há vendas em ${selectedCurrency} no período selecionado.`}
            />
          )
        ) : (
          <>
            {visibleRows.length > 0 ? (
              <SalesTable
                rows={visibleRows}
                onSelect={selectSale}
                onViewSubscription={onViewSubscription}
              />
            ) : (
              <EmptyState
                icon={view === 'renewals' ? <Repeat size={22} /> : <ShoppingBag size={22} />}
                title={view === 'renewals' ? 'Nenhuma renovação' : 'Nenhuma venda nova'}
                description={
                  sales.hasNextPage
                    ? 'Nada nas vendas já carregadas: use "Carregar mais" para continuar procurando.'
                    : view === 'renewals'
                      ? 'As renovações de assinatura aparecem aqui quando houver cobranças recorrentes.'
                      : 'As vendas novas (não recorrentes) aparecem aqui.'
                }
              />
            )}
            <LoadMore
              hasMore={sales.hasNextPage}
              loading={sales.isFetchingNextPage}
              shown={visibleRows.length}
              onClick={() => {
                void sales.fetchNextPage()
              }}
            />
          </>
        )}
      </Card>

      <SaleDetailDrawer scope={search.scope} saleId={search.sale ?? null} onClose={closeSale} />
    </div>
  )
}
